// @ts-check

import { debounce } from '../common';

// TODO: Use IntersectionObserver instead of Scroll Event Listener
// Can track visibility of an element at the top of the page to determine
// if the page has been scrolled (e.g. skip-to-content link)
// and then update body attributes and header group based on
// visibility of the observed element

const constants = Object.freeze({
  none: 'none',
  static: 'static',
  sticky: 'sticky',
  fixed: 'fixed',
  dynamic: 'dynamic',
  siteHeader: 'site-header',
  dynamicHidden: 'dynamic_hidden',
  umgSticky: 'umg-sticky',
  headerGroupSelector: '.shopify-section-group-header-group'
});

/**
 * Represents one header element within the header group
 */
class Header {
  /**
   * @param {HTMLElement} element
   */
  constructor(element) {
    /**
     * The matching header element
     * @type {HTMLElement}
     */
    this.element = element;

    /**
     * Expresses whether or not the header is of type SiteHeader
     * @type {boolean}
     */
    this.isSiteHeader = false;

    /**
     * Makes computed style of the header easily accessible
     * @type {CSSStyleDeclaration}
     */
    this.computedStyle = window.getComputedStyle(this.element);

    /**
     * Expresses whether or not the header is position: sticky
     * @type {boolean}
     */
    this.isSticky = this.computedStyle.position === constants.sticky;

    /**
     * Expresses whether or not the header is position: fixed
     * @type {boolean}
     */
    this.isFixed = this.computedStyle.position === constants.fixed;

    /**
     * Expresses whether or not the header is display: none
     * @type {boolean}
     */
    this.isHidden = this.computedStyle.display === constants.none;

    /**
     * Expresses whether or not the header is dynamically hidden (via transform: translate)
     * @type {boolean}
     */
    this.isDynamicHidden = false;
    this.resetStyles();
    this.resetVars();
  }

  /**
   * resets the header styles to build headers
   * @returns {void}
   */
  resetStyles() {
    this.element.classList.remove(constants.dynamicHidden);
    this.element.style.transform = '';
    this.element.style.top = '';
    this.element.classList.add(constants.umgSticky, 'transition-[transform]');
  }

  /**
   * resets the header variables to build headers
   * @returns {void}
   */
  resetVars() {
    this.computedStyle = window.getComputedStyle(this.element);
    this.isSticky = this.computedStyle.position === constants.sticky;
    this.isFixed = this.computedStyle.position === constants.fixed;
    this.isHidden = this.computedStyle.display === constants.none;
    this.isDyanmicHidden = false;
  }

  /**
   * Dynamically shows the header element
   * @returns {void}
   */
  dynamicShow() {
    if (this.isSiteHeader || !this.isSticky) {
      this.element.classList.remove(constants.dynamicHidden);
      this.element.style.transform = `translateY(0)`;
      this.isDynamicHidden = false;
    }
  }

  /**
   * Dynamically hides the header element
   * @returns {void}
   */
  dynamicHide() {
    if (this.isSiteHeader || !this.isSticky) {
      this.element.classList.add(constants.dynamicHidden);
      this.element.style.transform = `translateY(calc(-1 * var(--header-group-height)))`;
      this.isDynamicHidden = true;
    }
  }
}

/**
 * Represents a main navigation site header
 * @extends {Header}
 */
class SiteHeader extends Header {
  /**
   * @param {HTMLElement} element
   */
  constructor(element) {
    super(element);
    this.isSiteHeader = true;

    /**
     * The section wrapper of the site header contains position and behavior data
     * @type {HTMLElement?}
     */
    this.wrapper = this.element.querySelector('.header__wrapper');

    /**
     * Expresses whether or not this header is meant for tablet+ screens
     * @type {boolean}
     */
    this.isMd = this.wrapper === null ? false : this.wrapper.classList.contains('header__wrapper--md');

    /**
     * Expresses the position and behavior settings derived from theme settings
     * @type {IBehaviors}
     */
    this.behaviors = this.setBehaviors();
    this.updateHeaderPositions();
    this.resetVars();
  }

  /**
   * Sets behaviors of the site header by parsing position data on the section wrapper
   * @returns {IBehaviors}
   */
  setBehaviors() {
    /** @type {IBehaviors} */
    const behaviors = {
      dynamic: false,
      headerPosition: constants.static,
      groupPosition: constants.static
    };
    const behaviorString = this.wrapper?.dataset.position ?? null;
    const [dynamicPart, positionPart] = behaviorString?.split('_') ?? [null, null];
    if (!dynamicPart || !positionPart) return behaviors;
    behaviors.dynamic = dynamicPart === constants.dynamic;
    behaviors.headerPosition = positionPart;
    behaviors.groupPosition = positionPart === constants.fixed ? constants.fixed : constants.static;
    return behaviors;
  }

  /**
   * Updates css variables for site header and header group positions
   * @returns {void}
   */
  updateHeaderPositions() {
    const modifier = this.isMd ? '-md' : '';
    document.body.style.setProperty(`--header-group-header-position${modifier}`, this.behaviors.headerPosition);
    document.body.style.setProperty(`--header-group-position${modifier}`, this.behaviors.groupPosition);
  }
}

/**
 * Represents a sticky element in the DOM
 */
class StickyElement {
  /**
   * @constructor
   * @param {HTMLElement} element
   */
  constructor(element) {
    this.element = element;
    this.computedStyle = window.getComputedStyle(this.element);
    this.isHidden = this.computedStyle.display === constants.none;
    this.isSticky = this.computedStyle.position === constants.sticky;
    this.isFixed = this.computedStyle.position === constants.fixed;
  }
}

/**
 * Represents all header elements as a single group
 */
class HeaderGroup {
  /**
   * @constructor
   * @param {Header[]} headers
   */
  constructor(headers) {
    /**
     * The collection of Headers within the header group
     * @type {Header[]}
     */
    this.headers = headers;

    /**
     * The collection of SiteHeaders within the header group's headers
     * @type {SiteHeader[]}
     */
    this.siteHeaders = /** @type {SiteHeader[]} */this.headers.filter(header => header.isSiteHeader);

    /**
     * The collection of all sticky elements in the document
     * @type {StickyElement[]}
     */
    this.stickyElements = [];

    /**
     * Expresses whether or not the header group contains a fixed header
     * @type {boolean}
     */
    this.fixedGroup = this.siteHeadersBehave('headerPosition', 'fixed');

    /**
     * Expresses whether or not the header group has dynamic headers
     * @type {boolean}
     */
    this.hasDynamicHeaders = this.siteHeadersBehave('dynamic', true);

    /**
     * The collection of target headers to dynamically hide / show on scroll up / down
     * In the case that the header group contains a dynamic header
     * @type {Header[]}
     */
    this.dynamicTargets = [];

    /**
     * The scroll event callback to be stored so that it can also be removed when headers are rebuilt
     * @type {EventListener?}
     */
    this.scrollEventCallback = null;
  }

  /**
   * Returns whether or not header group site headers have a certain behavior value
   * @param {string} name - the name of the behavior
   * @param {any} value
   * @returns {boolean}
   */
  siteHeadersBehave(name, value) {
    for (const header of this.siteHeaders) {
      if (!header.isHidden && header.behaviors[name] === value) {
        return true;
      }
    }
    return false;
  }

  /**
   * Dynamically hides all headers within the group's dynamic targets
   * @returns {void}
   */
  hideDynamicTargets() {
    for (const target of this.dynamicTargets) {
      target.dynamicHide();
      this.updateMeasurements();
    }
  }

  /**
   * Dynamically show all headers within the group's dynamic targets
   * @returns {void}
   */
  showDynamicTargets() {
    for (const target of this.dynamicTargets) {
      target.dynamicShow();
      this.updateMeasurements();
    }
  }

  /**
   * Updates the measurements of the header group on the initial run
   * @returns {void}
   */
  updateInitialMeasurements() {
    return this.updateMeasurements(true);
  }

  /**
   * Sets css variables that control fixed and sticky heights of header group elements
   * @param {boolean} isInitRun - (optional) whether this is the initial run, will reset header initial indices. default is `false`.
   * @returns {void}
   */
  updateMeasurements() {
    let isInitRun = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
    let headerGroupHeight = 0;
    let headerVisibleHeight = 0;
    let headerStickyHeight = 0;
    let i = 0;
    const elementsChecked = [];
    for (const header of this.headers) {
      if (!header.isHidden) {
        let headerHeight = header.element.offsetHeight;
        if (headerHeight === 0) {
          headerHeight = header.element.querySelector('div')?.offsetHeight ?? 0;
        }
        headerGroupHeight += headerHeight;
        if (!header.isDynamicHidden) {
          headerVisibleHeight += headerHeight;
          if (header.isSticky || header.isFixed) {
            headerStickyHeight += headerHeight;
          }
        }
        document.body.style.setProperty(`--header-item-height-${i}`, headerVisibleHeight + 'px');
        document.body.style.setProperty(`--header-item-sticky-height-${i}`, headerStickyHeight + 'px');
        header.element.style.top = i === 0 ? '0' : header.isSticky ? `var(--header-item-sticky-height-${i - 1})` : `var(--header-item-height-${i - 1})`;
        i++;
      }
      elementsChecked.push(header.element);
    }
    for (const stickyElement of this.stickyElements) {
      if (!elementsChecked.includes(stickyElement.element)) {
        if (!stickyElement.isHidden) {
          let headerHeight = stickyElement.element.offsetHeight;
          if (headerHeight === 0) {
            headerHeight = stickyElement.element.querySelector('div')?.offsetHeight ?? 0;
          }
          if (stickyElement.isSticky || stickyElement.isFixed) {
            headerStickyHeight += headerHeight;
          }
          document.body.style.setProperty(`--header-item-sticky-height-${i}`, headerStickyHeight + 'px');
          stickyElement.element.style.top = i === 0 ? '0' : `var(--header-item-sticky-height-${i - 1})`;
          i++;
        }
        elementsChecked.push(stickyElement.element);
      }
    }
    document.body.style.setProperty('--header-group-height', headerGroupHeight + 'px');
    document.body.style.setProperty('--header-sticky-height', headerStickyHeight + 'px');
    if (isInitRun) {
      document.body.style.setProperty('--header-group-height-init', headerGroupHeight + 'px');
    }
  }

  /**
   * Sets the scroll listener for headers
   * @returns {void}
   */
  setScrollListener() {
    const initialScrollPosition = window.scrollY;
    for (const header of this.siteHeaders) {
      const isDynamicHeader = header.isSiteHeader && header.behaviors.dynamic;
      if (!header.isHidden && isDynamicHeader) {
        this.dynamicTargets = header.isFixed ? this.headers : [header];
        this.scrollEventCallback = scrollEventListener(initialScrollPosition, this);
        window.addEventListener('scroll', this.scrollEventCallback, {
          passive: true
        });
      }
    }
  }
}

/**
 * A singleton that manages the header group
 *
 * @example
 * const headerGroupManager = HeaderGroupManager.getInstance()
 */
export class HeaderGroupManager {
  /**
   * @private
   */
  constructor() {
    /**
     * The header group
     * @type {HeaderGroup?}
     */
    this.headerGroup = null;

    /**
     * The collection of all sticky elements in the document
     * @type {StickyElement[]}
     */
    this.stickyElements = [];
    document.addEventListener('DOMContentLoaded', () => this.buildInitialHeaders());
    window.addEventListener('resize', debounce(() => this.buildHeaders(false), 100));
    // @ts-expect-error - Shopify global
    if (Shopify.designMode) {
      document.addEventListener('shopify:section:load', () => this.buildInitialHeaders());
      document.addEventListener('shopify:section:unload', () => this.buildInitialHeaders());
      document.addEventListener('shopify:section:reorder', () => this.buildInitialHeaders());
    }
  }

  /**
   * Returns the singleton instance of the header group manager
   * @returns {HeaderGroupManager}
   */
  static getInstance() {
    if (!HeaderGroupManager.instance) {
      HeaderGroupManager.instance = new HeaderGroupManager();
    }
    return HeaderGroupManager.instance;
  }

  /**
   * Builds the initial headers
   * @returns {void}
   */
  buildInitialHeaders() {
    return this.buildHeaders(true);
  }

  /**
   * Primary trigger function, rebuilds the entire header group
   * @param {boolean} isInitRun - whether this is the initial run, will reset header initial indices
   * @returns {void}
   */
  buildHeaders() {
    let isInitRun = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
    if (this.headerGroup?.scrollEventCallback) {
      window.removeEventListener('scroll', this.headerGroup.scrollEventCallback);
    }

    /** @type {NodeListOf<HTMLElement>} */
    const headerGroupElements = document.querySelectorAll(constants.headerGroupSelector);
    const headerGroupArray = [];
    for (const [index, headerElement] of headerGroupElements.entries()) {
      const header = headerElement.classList.contains(constants.siteHeader) ? new SiteHeader(headerElement) : new Header(headerElement);
      if (isInitRun) {
        header.element.dataset.initialIndex = index.toString();
      }
      headerGroupArray.push(header);
    }
    const headerGroup = new HeaderGroup(headerGroupArray);
    this.stickyHeaderOrderManager(headerGroup);
    headerGroup.updateInitialMeasurements();
    if (headerGroup.hasDynamicHeaders) {
      headerGroup.setScrollListener();
    }
    this.headerGroup = headerGroup;
    this.setStickyElements();
    this.headerGroup.stickyElements = this.stickyElements;
  }

  /**
   * Collect sticky elements on the DOM into the stickyElements collection
   * @returns {void}
   */
  setStickyElements() {
    const stickyElements = /** @type {HTMLElement[]} */Array.from(document.querySelectorAll('.' + constants.umgSticky)).filter(element => {
      return window.getComputedStyle(element).position === constants.sticky;
    });
    const stickyElementsArray = [];
    for (const element of stickyElements) {
      stickyElementsArray.push(new StickyElement(element));
    }
    this.stickyElements = stickyElementsArray;
  }

  /**
   * If a header is sticky and the header group is fixed
   * then move the header to the top of the header group
   * otherwise, move it back to where it was initially
   * @param {HeaderGroup} headerGroup
   * @returns {void}
   */
  stickyHeaderOrderManager(headerGroup) {
    if (headerGroup !== null && typeof headerGroup !== 'undefined') {
      let staticIndexModifier = 0;
      for (const header of [...headerGroup.headers].reverse()) {
        headerGroup.headers.splice(headerGroup.headers.indexOf(header), 1);
        if (headerGroup.fixedGroup && header.isSticky) {
          headerGroup.headers.unshift(header);
          staticIndexModifier += 1;
        } else {
          const indexToSplice = header.element.dataset.initialIndex;
          if (!indexToSplice) return;
          headerGroup.headers.splice(parseInt(indexToSplice, 10) + staticIndexModifier, 0, header);
        }
      }
    }
  }

  /**
   * Updates the measurements of the header group
   * @returns {void}
   */
  updateMeasurements() {
    return this.headerGroup?.updateMeasurements();
  }
}

/**
 * @type {HeaderGroupManager?}
 */
HeaderGroupManager.instance = null;

/**
 * On scroll up, move dynamic header group elements out of view
 * On scroll down, bring the elements back into view
 * @param {number} initialScrollPosition
 * @param {HeaderGroup} headerGroup
 * @returns {EventListener}
 */
const scrollEventListener = (initialScrollPosition, headerGroup) => {
  let previousScrollPosition = initialScrollPosition;
  return () => {
    const currentScrollPosition = window.scrollY;
    const isScrollingUp = previousScrollPosition > currentScrollPosition;
    document.body.dataset.scrolled = String(window.scrollY > 0);

    /** @type {NodeListOf<HTMLElement & {close: () => void}>} */
    const predictiveSearches = document.querySelectorAll('.' + constants.siteHeader + ' predictive-search');
    for (const search of predictiveSearches) {
      search.close();
    }
    if (isScrollingUp && document.body.dataset.scrollDirection !== 'up') {
      headerGroup.showDynamicTargets();
      document.body.dataset.scrollDirection = 'up';
    } else if (!isScrollingUp && document.body.dataset.scrollDirection !== 'down') {
      // Hide header if we arent at the top of the page
      if (window.scrollY <= 0) return;
      headerGroup.hideDynamicTargets();
      document.body.dataset.scrollDirection = 'down';
    }
    previousScrollPosition = currentScrollPosition;
  };
};

/**
 * @typedef {{
 *   dynamic: boolean,
 *   headerPosition: string,
 *   groupPosition: string,
 * }} IBehaviors
 */